import { Logo } from "../../logo/Logo";
import { ThemeProvider } from "../../themeSwitcher/ThemeProvider";
import {
  appErrorContentContainer,
  appErrorMainContent,
  appErrorPage,
  appErrorPageWithBackground,
  copyrightText,
  logoContainer
} from "./AppFallback.css";
import { BaseFallback } from "./BaseFallback";

/**
 * Props for error fallback components.
 */
export interface AppFallbackProps {
  /**
   * The error that was caught
   */
  error: Error;

  /** The level of the error boundary (app, route, or component) */
  level?: "app" | "route";

  /**
   * Display error
   */
  displayError?: boolean;
}

/**
 * Unified error fallback component for app and route level errors.
 *
 * - App level: Full page layout with logo, copyright, wrapped in ThemeProvider
 * - Route level: Simplified layout without logo/copyright, no ThemeProvider (already exists in context)
 */
export function AppFallback({
  error,
  level = "app",
  displayError
}: AppFallbackProps) {
  const isAppLevel = level === "app";

  const content = (
    <div className={isAppLevel ? appErrorPageWithBackground : appErrorPage}>
      <div className={appErrorMainContent}>
        <div className={appErrorContentContainer}>
          {isAppLevel && (
            <div className={logoContainer}>
              <Logo />
            </div>
          )}
          <BaseFallback
            error={error}
            displayErrorInfo={displayError}
            displayActionButtons
          />
        </div>
      </div>
      {isAppLevel && (
        <p className={copyrightText}>© Appretio {new Date().getFullYear()}</p>
      )}
    </div>
  );

  // Only wrap with ThemeProvider for app-level errors
  return isAppLevel ? <ThemeProvider>{content}</ThemeProvider> : content;
}
